'use client';

import React, { useState, useMemo } from 'react';
import { cm } from 'src/utils/classNames';
import { LoadingChart } from '../LoadingChart';
import {
	BigNum,
	BN,
	calculateScaledInitialAssetWeight,
	ZERO,
	getTokenAmount,
	getTokenValue,
	SpotBalanceType,
} from '@velocity-exchange/sdk';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useDriftClient from 'src/hooks/useDriftClient';

import {
	Line,
	LineChart,
	CartesianGrid,
	ResponsiveContainer,
	XAxis,
	YAxis,
	Tooltip as ChartsTooltip,
	ReferenceLine,
	LabelProps,
} from 'recharts';

import type { CartesianViewBox } from 'recharts/types/util/types';
import { getColorVar } from 'src/utils/theme/colorVars';
import { calculateYAxisWidth } from 'src/utils/graph';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import { TabOption } from 'src/components/primitives/Tabs/types';
import Link from 'src/components/primitives/Link/Link';
import {
	generateIndividualChartData,
	calculatePureIndividualAssetWeight,
} from './generateIndividualChartData';
import { generateGlobalChartData } from './generateGlobalChartData';
import { millify } from '@velocity-exchange/common';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';

type ChartTabValue = 'individual' | 'global';

type IAWDataPoint = {
	x: number;
	y: number;
};

const TabOptions: TabOption<ChartTabValue>[] = [
	{ value: 'global', label: 'Global' },
	{ value: 'individual', label: 'Individual' },
];

interface InitialAssetWeightGraphProps {
	marketIndex: number;
	userPositionSize?: BigNum;
	className?: string;
}

const CustomTooltip = ({
	active,
	payload,
	isIndividual,
	baseSymbol,
}: {
	active?: boolean;
	payload?: Array<{ payload?: IAWDataPoint }>;
	isIndividual: boolean;
	baseSymbol: string;
}) => {
	if (!active || !payload || !payload.length) {
		return null;
	}

	const data = payload[0].payload as IAWDataPoint;

	return (
		<div className="flex flex-col gap-y-xs rounded-md bg-tooltip-bg p-sm">
			<div className="flex items-center justify-between gap-x-lg">
				<span className="typo-body-xs text-text-default">
					{isIndividual ? 'User Deposit' : 'Total Deposit'}
				</span>
				<span className="typo-body-xs text-text-default">
					{isIndividual
						? `${millify(data.x, { trimEndingZeroes: true })} ${baseSymbol}`
						: `$${millify(data.x, { trimEndingZeroes: true })}`}
				</span>
			</div>
			<div className="flex items-center justify-between gap-x-lg">
				<span className="typo-body-xs text-text-default">
					Initial Asset Weight
				</span>
				<span className="typo-body-xs text-text-default">
					{data.y.toFixed(2)}%
				</span>
			</div>
		</div>
	);
};

const CustomizedLabel = ({
	viewBox,
	isIndividual,
	baseSymbol,
	currentValue,
}: LabelProps & {
	isIndividual: boolean;
	baseSymbol: string;
	currentValue: number;
}) => {
	const viewBoxTyped = viewBox as CartesianViewBox;

	const viewBoxX = viewBoxTyped.x ?? 0;
	const viewBoxHeight = viewBoxTyped.height ?? 0;

	const x = viewBoxX + 10;
	const y = viewBoxHeight - 30;

	const labelLine1 = isIndividual ? 'Your Deposit' : 'Current Deposit';
	const labelLine2 = isIndividual
		? `${millify(currentValue, { trimEndingZeroes: true })} ${baseSymbol}`
		: `$${millify(currentValue, { trimEndingZeroes: true })}`;

	return (
		<text
			x={x}
			y={y}
			fill={getColorVar('text-secondary')}
			fontSize={10}
			textAnchor="start"
		>
			<tspan x={x} dy={0}>
				{labelLine1}
			</tspan>
			<tspan x={x} dy={12}>
				{labelLine2}
			</tspan>
		</text>
	);
};

export const InitialAssetWeightGraph = ({
	marketIndex,
	userPositionSize,
	className,
}: InitialAssetWeightGraphProps) => {
	const [activeTab, setActiveTab] = useState<ChartTabValue>('global');
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();

	const spotMarketAccount = driftClient?.getSpotMarketAccount(marketIndex);
	const individualChartData = useMemo(
		() => generateIndividualChartData(spotMarketAccount),
		[spotMarketAccount]
	);

	const oraclePriceData = driftClient?.getOracleDataForSpotMarket(marketIndex);
	const globalChartData = useMemo(
		() => generateGlobalChartData(spotMarketAccount, oraclePriceData?.price),
		[spotMarketAccount, oraclePriceData?.price]
	);

	if (
		!driftClientIsReady ||
		!driftClient ||
		!spotMarketAccount ||
		!oraclePriceData
	) {
		return <LoadingChart />;
	}

	const baseSymbol = spotMarketAccount.name
		.map((n) => String.fromCharCode(n))
		.join('')
		.trim();

	const isIndividual = activeTab === 'individual';
	const currentPosition = userPositionSize?.val ?? ZERO;

	const chartData = isIndividual ? individualChartData : globalChartData;

	// Calculate current values for reference lines
	const currentPositionInBase =
		currentPosition.toNumber() / Math.pow(10, spotMarketAccount.decimals);

	const deposits = getTokenAmount(
		spotMarketAccount.depositBalance,
		spotMarketAccount,
		SpotBalanceType.DEPOSIT
	);
	const currentDepositsValue = getTokenValue(
		deposits,
		spotMarketAccount.decimals,
		{
			price: oraclePriceData.price,
		}
	);
	const currentDepositsUSD = currentDepositsValue.toNumber() / 1_000_000;

	// Calculate current IAW values
	const currentIndividualIAW = userPositionSize
		? calculatePureIndividualAssetWeight(currentPosition, spotMarketAccount)
		: new BN(spotMarketAccount.initialAssetWeight);

	// Calculate current global IAW (pure global scaling without size discount)
	const currentGlobalIAW = calculateScaledInitialAssetWeight(
		spotMarketAccount,
		oraclePriceData.price
	);

	const referenceValue = isIndividual
		? currentPositionInBase
		: currentDepositsUSD;

	const finalIAWBigNum = BigNum.from(
		Math.min(currentIndividualIAW.toNumber(), currentGlobalIAW.toNumber())
	).div(BigNum.from(100));

	const currentGlobalIAWBigNum = BigNum.from(currentGlobalIAW.toNumber()).div(
		BigNum.from(100)
	);

	const currentIndividualIAWBigNum = BigNum.from(
		currentIndividualIAW.toNumber()
	).div(BigNum.from(100));

	const yAxisWidth = (() => {
		const maxY = Math.max(...chartData.map((d) => d.y));
		const labels = [0, Math.ceil(maxY)].map((v) => `${v}%`);
		return calculateYAxisWidth(labels);
	})();

	return (
		<div className={cm('flex flex-col gap-4', className)}>
			<div className="typo-body-xs text-text-secondary">
				Initial Asset Weight (IAW) is the smaller value of the two Global and
				Individual values. Your IAW for {baseSymbol} for this subaccount is
				currently{' '}
				<span className="text-text-default">
					{finalIAWBigNum.printShort()}%
				</span>
				.{' '}
				<Link
					href="https://docs.drift.trade/getting-started/cross-collateral-deposits"
					openInNewTab
					variant="tertiary"
				>
					Learn more
				</Link>
			</div>

			<Tabs
				variant="secondary"
				options={TabOptions}
				activeTab={activeTab}
				size="sm"
				onTabChange={setActiveTab}
			/>

			{!isIndividual && (
				<div className="flex items-center justify-between">
					<div className="typo-body-xs">
						<span className="text-text-secondary">Total Deposit: </span>
						<span className="text-text-default">
							${currentDepositsUSD.toLocaleString()}
						</span>
					</div>
					<div className="typo-body-xs">
						<span className="text-text-secondary">Global IAW: </span>
						<span className="text-text-default">
							{currentGlobalIAWBigNum.printShort()}%
						</span>
					</div>
				</div>
			)}

			{isIndividual && (
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-x-xs typo-body-xs">
						<span className="text-text-secondary">Your Deposit: </span>
						<span className="flex items-center gap-x-xs text-text-default">
							<NumberDisplay
								value={BigNum.from(currentPosition, spotMarketAccount.decimals)}
								type="number"
								precision="trade"
							/>{' '}
							{baseSymbol}
						</span>
					</div>
					<div className="typo-body-xs">
						<span className="text-text-secondary">Individual IAW: </span>
						<span className="text-text-default">
							{currentIndividualIAWBigNum.printShort()}%
						</span>
					</div>
				</div>
			)}

			<ResponsiveContainer width="100%" height={200}>
				<LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5 }}>
					<CartesianGrid
						stroke={getColorVar('stroke-divider')}
						vertical={false}
					/>

					<XAxis
						dataKey="x"
						type="number"
						domain={[0, 'dataMax']}
						stroke={getColorVar('stroke-divider')}
						tickLine={false}
						tick={{ fontSize: 10 }}
						tickFormatter={(value) =>
							isIndividual
								? millify(value, { trimEndingZeroes: true })
								: `$${millify(value, { trimEndingZeroes: true })}`
						}
					/>
					<YAxis
						width={yAxisWidth}
						stroke={getColorVar('stroke-divider')}
						tickLine={false}
						tick={{ fontSize: 10 }}
						tickFormatter={(value) => `${value.toFixed(0)}%`}
					/>
					<ChartsTooltip
						content={(props) => (
							<CustomTooltip
								{...props}
								isIndividual={isIndividual}
								baseSymbol={baseSymbol}
							/>
						)}
						cursor={{
							stroke: getColorVar('text-secondary'),
							strokeDasharray: '4',
						}}
					/>
					<Line
						type="monotone"
						dataKey="y"
						stroke={getColorVar('text-positive-green-button')}
						strokeWidth={2}
						dot={false}
						animationDuration={300}
					/>
					{referenceValue > 0 && (
						<ReferenceLine
							x={referenceValue}
							stroke={getColorVar('text-secondary')}
							strokeWidth={1}
							label={
								<CustomizedLabel
									isIndividual={isIndividual}
									baseSymbol={baseSymbol}
									currentValue={
										isIndividual ? currentPositionInBase : currentDepositsUSD
									}
								/>
							}
						/>
					)}
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
};
